import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Github, ExternalLink } from "lucide-react";

const Projects = () => {
  const [filter, setFilter] = useState("all");

  const projects = [
    {
      id: 1,
      title: "CodeLens: Multi-Model RAG over GitHub",
      lead: "Python · LangChain · Ollama · Qdrant · FastAPI",
      description:
        "Multi-model RAG system over GitHub repositories with dynamic model routing via a LangChain orchestrator that selects task-specific local Ollama models (Gemma for code, instruction-tuned for writing). Hybrid retrieval (HNSW + BM25) on 768-dim embeddings with tuned ef_construction / ef_search delivers sub-100ms p95 retrieval latency for repo-aware Q&A.",
      image: "🧭",
      category: "llm-systems",
      technologies: [
        "Python",
        "LangChain",
        "Ollama",
        "Qdrant",
        "HNSW",
        "FastAPI",
        "Multi-Agent",
      ],
      github: "https://github.com/SatyaChamana/Codelens",
    },
    {
      id: 2,
      title: "FinSight: Multi-Tenant Stock Forecasting",
      lead: "Python · PyTorch (LSTM) · FastAPI · Yahoo Finance API",
      description:
        "LSTM-based time-series forecasting integrating Yahoo Finance price data with real-time news sentiment signals, dynamically reweighting forecasts based on market events. Designed for multi-tenant portfolio analysis with per-user isolation, async data ingestion, and indexed time-series storage for low-latency prediction queries across concurrent users.",
      image: "📈",
      category: "ai-ml",
      technologies: [
        "Python",
        "PyTorch",
        "LSTM",
        "FastAPI",
        "Multi-Tenant",
        "Async I/O",
      ],
      github: "https://github.com/SatyaChamana/FinSight",
    },
    {
      id: 3,
      title: "Agentic PKM System + graphify",
      lead: "Python · Claude Code SDK · Knowledge Graphs · TF-IDF",
      description:
        "Self-built agentic knowledge platform on Claude Code with 6 custom skills, 10 specialized analyst roles (DCF, risk, screening), and persistent YAML-frontmatter memory. graphify is the public companion: a knowledge-graph compiler (687 nodes, 918 edges, 45 communities) that turns any folder of code, docs, papers, or images into a queryable index. 11 stdlib-only Python CLIs across 2,850 LOC.",
      image: "🧠",
      category: "dev-tools",
      technologies: [
        "Python (stdlib)",
        "Claude Code SDK",
        "Knowledge Graphs",
        "TF-IDF",
        "Git Hooks",
        "Headless Chrome",
      ],
      github: "https://github.com/SatyaChamana/graphify",
    },
    {
      id: 4,
      title: "Hospital Readmission & Patient Analytics",
      lead: "Azure SQL · Power BI · DAX",
      description:
        "End-to-end healthcare analytics on Massachusetts General Hospital data analyzing 30-day readmissions, financial performance, and patient demographics. Star schema on Azure SQL with window functions (LEAD) for readmission detection. 4-page interactive Power BI dashboard with 24+ DAX measures and drill-through navigation, aligned with the CMS HRRP framework.",
      image: "🏥",
      category: "data-analytics",
      technologies: [
        "Azure SQL",
        "Power BI",
        "DAX",
        "SQL",
        "Star Schema",
        "ETL",
      ],
      github: "https://github.com/SatyaChamana/hospital-readmission-analytics",
    },
    {
      id: 5,
      title: "Real-Time Polyp Detection (Full-Stack CV)",
      lead: "TensorFlow · React · Express · D3.js · OpenCV",
      description:
        "Full-stack medical AI system for real-time polyp detection in colonoscopy videos. CNN trained on 2,000+ clinical images at 93% accuracy. React frontend, Python/TensorFlow backend, Express inference endpoints, and D3.js for confidence scores and frame-by-frame analysis.",
      image: "🩺",
      category: "computer-vision",
      technologies: [
        "Python",
        "TensorFlow",
        "OpenCV",
        "React",
        "Express",
        "D3.js",
        "CNN",
      ],
      github: "https://github.com/SatyaChamana/Colonoscopy-Image-Processing",
    },
    {
      id: 6,
      title: "FuelFlow: Drive-Thru Vehicle & Fuel Analysis",
      lead: "YOLOv8 · DeepSORT · OpenCV",
      description:
        "Vehicle detection and tracking in drive-thru environments using YOLOv8 and DeepSORT. 92% detection accuracy at 25+ FPS across 1,000+ video frames. Estimated wait times and idle fuel consumption, identifying 25% higher fuel waste in drive-thru vs in-person across 5 vehicle types.",
      image: "⛽",
      category: "computer-vision",
      technologies: ["Python", "YOLOv8", "OpenCV", "DeepSORT"],
    },
  ];

  const categories = [
    { key: "all", label: "All Projects" },
    { key: "llm-systems", label: "LLM & RAG Systems" },
    { key: "ai-ml", label: "AI & Machine Learning" },
    { key: "computer-vision", label: "Computer Vision" },
    { key: "data-analytics", label: "Data Analytics" },
    { key: "dev-tools", label: "Dev Tools" },
  ];

  const filteredProjects =
    filter === "all"
      ? projects
      : projects.filter((project) => project.category === filter);

  return (
    <div className="px-[5vw] pt-16 pb-20" style={{ color: "var(--text)" }}>
      <div className="max-w-3xl">
        <h1 className="text-4xl font-semibold md:text-5xl">Projects</h1>
        <p className="mt-3" style={{ color: "var(--text-muted)" }}>
          End-to-end production AI systems: RAG pipelines, multi-agent
          orchestration, computer vision, and the dev tooling that holds it
          together.
        </p>
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        {categories.map((category) => (
          <button
            key={category.key}
            className="rounded-full px-5 py-2 text-sm font-semibold transition border"
            style={
              filter === category.key
                ? {
                    backgroundColor: "var(--accent)",
                    color: "var(--accent-foreground)",
                    borderColor: "var(--accent)",
                  }
                : {
                    backgroundColor: "transparent",
                    color: "var(--text-muted)",
                    borderColor: "var(--border)",
                  }
            }
            onClick={() => setFilter(category.key)}
          >
            {category.label}
          </button>
        ))}
      </div>

      <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredProjects.map((project) => (
          <article
            key={project.id}
            className="flex flex-col rounded-3xl border p-6"
            style={{
              backgroundColor: "var(--card)",
              borderColor: "var(--border)",
            }}
          >
            <span className="text-3xl">{project.image}</span>
            <h3 className="mt-4 text-lg font-semibold leading-snug">
              {project.title}
            </h3>
            <p
              className="mt-1 text-xs font-semibold"
              style={{ color: "var(--accent)" }}
            >
              {project.lead}
            </p>
            <p
              className="mt-3 text-sm leading-relaxed"
              style={{ color: "var(--text-muted)" }}
            >
              {project.description}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {project.technologies.map((tech) => (
                <span
                  key={tech}
                  className="rounded-full px-3 py-1 text-[0.7rem]"
                  style={{
                    backgroundColor: "var(--pill)",
                    color: "var(--text-muted)",
                  }}
                >
                  {tech}
                </span>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap gap-2 pt-2">
              {project.github && (
                <a
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold"
                  style={{
                    borderColor: "var(--border)",
                    color: "var(--text)",
                  }}
                >
                  <Github size={14} />
                  Code
                </a>
              )}
              {project.demo && (
                <a
                  href={project.demo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold"
                  style={{
                    borderColor: "var(--border)",
                    color: "var(--text)",
                  }}
                >
                  <ExternalLink size={14} />
                  Live
                </a>
              )}
            </div>
          </article>
        ))}
      </div>

      <div className="mt-10 flex justify-center">
        <a
          href="https://github.com/SatyaChamana"
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-3 rounded-2xl border px-8 py-4 text-base font-semibold backdrop-blur-sm transition-all duration-300"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--pill)",
            color: "var(--text)",
          }}
        >
          <Github
            size={22}
            className="transition-transform duration-300 group-hover:scale-110"
          />
          <span>Explore All Repos on GitHub</span>
          <ArrowUpRight
            size={16}
            className="opacity-50 transition-all duration-300 group-hover:opacity-100"
          />
        </a>
      </div>

      <div
        className="mt-12 rounded-3xl px-6 py-8 text-center"
        style={{
          backgroundColor: "var(--accent)",
          color: "var(--accent-foreground)",
        }}
      >
        <h3 className="text-2xl font-semibold">
          Looking for a Senior AI/ML Engineer?
        </h3>
        <p className="mt-2 text-sm">
          Eight years of production engineering. RAG at sub-50ms. ML at 92%
          accuracy. Open to senior AI/ML, ML platform, and applied AI roles in
          the Bay Area.
        </p>
        <Link
          to="/contact"
          className="mt-2 inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold"
          style={{
            backgroundColor: "var(--bg)",
            color: "var(--text)",
          }}
        >
          Let's Connect
          <ArrowUpRight size={16} />
        </Link>
      </div>
    </div>
  );
};

export default Projects;
